/**
 * @file
 * Slim Select (https://slimselectjs.com/) multiselect component behavior.
 *
 * Slim Select is bundled from this theme's own `slim-select` npm dependency
 * (see package.json) rather than the Drupal `slim_select` contrib module, so
 * the library version and its configuration live entirely in the front end.
 * The `SlimSelect` global below is provided by
 * `node_modules/slim-select/dist/slimselect.js`, loaded ahead of this file
 * by the `hab_theme/slim-select` library (see hab_theme.libraries.yml).
 *
 * @see https://slimselectjs.com/select
 */
!((Drupal, once) => {
  'use strict';

  // Baseline settings applied to every [data-multiselect] element. Any
  // Slim Select setting (https://slimselectjs.com/select) can be overridden
  // per-element via a `data-multiselect-options` JSON attribute, or
  // programmatically through Drupal.habTheme.multiselect.create().
  const defaultSettings = {
    showSearch: true,
    placeholderText: Drupal.t('Select options...'),
    searchPlaceholder: Drupal.t('Search'),
    searchText: Drupal.t('No Results'),
    searchingText: Drupal.t('Searching...'),
  };

  /**
   * Parses the optional `data-multiselect-options` attribute.
   *
   * @param {HTMLSelectElement} selectElement
   *   The select element being enhanced.
   * @returns {object}
   *   Settings parsed from the attribute, or an empty object.
   */
  function readInlineSettings(selectElement) {
    const raw = selectElement.getAttribute('data-multiselect-options');
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw);
    }
    catch (error) {
      // eslint-disable-next-line no-console
      console.error('Invalid JSON in data-multiselect-options:', error);
      return {};
    }
  }

  /**
   * Instantiates Slim Select on a single element.
   *
   * Exposed on Drupal.habTheme.multiselect so callers who need full control
   * (custom events, async data, etc.) can build on the same defaults instead
   * of duplicating them.
   *
   * @param {HTMLSelectElement} selectElement
   *   The <select multiple> element to enhance.
   * @param {object} settings
   *   Settings merged on top of the defaults and any inline overrides -
   *   highest precedence.
   * @returns {SlimSelect}
   *   The Slim Select instance.
   */
  function create(selectElement, settings = {}) {
    return new SlimSelect({
      select: selectElement,
      settings: {
        ...defaultSettings,
        ...readInlineSettings(selectElement),
        ...settings,
      },
    });
  }

  Drupal.habTheme = Drupal.habTheme || {};
  Drupal.habTheme.multiselect = { create, defaultSettings };

  Drupal.behaviors.habThemeMultiselect = {
    attach(context) {
      once('hab-theme-multiselect', '[data-multiselect]', context).forEach(selectElement => {
        create(selectElement);
      });
    },
  };
})(Drupal, once);
